/**
 * Errors the API client raises. A single error type means callers can branch on
 * `status` instead of parsing messages, and it keeps HTTP vocabulary out of the
 * surfaces.
 */
export class ApiError extends Error {
  readonly status: number;
  /** Server-supplied correlation id, or the one the client generated. */
  readonly correlation_id: string;
  /** Parsed response body when the server sent one, for problem details. */
  readonly body: unknown;
  /**
   * CLM-65 — `exp` (seconds since the epoch) of the access token the client
   * attached to the failed request, decoded locally and unverified; `null` when
   * no token was attached or it could not be read. Recorded so a 401 can be
   * told apart as *expired* (sign in again) or *rejected while still valid*
   * (a configuration mismatch — signing in again will not help). Display only.
   */
  readonly token_exp: number | null;

  constructor(args: {
    message: string;
    status: number;
    correlation_id: string;
    body?: unknown;
    token_exp?: number | null;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.correlation_id = args.correlation_id;
    this.body = args.body;
    this.token_exp = args.token_exp ?? null;
  }

  /**
   * 403 means the gateway refused on role or scope grounds. It is the expected,
   * correct outcome when the SPA offers something the user may not do —
   * NFR-06 puts the decision there, so surfaces should present a 403 as a
   * normal state, not as a crash.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** 401 means the token was rejected; the caller should re-authenticate. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * Raised when a caller tries to send a scope-shaped parameter.
 *
 * Not an HTTP failure — a programming error caught before the request leaves
 * the browser. CLM party/practice scope is derived server-side from Entra group
 * claims; a client-supplied scope filter is exactly the input the gateway must
 * never trust, so the client refuses to construct the request at all.
 */
export class ClientSuppliedScopeError extends Error {
  readonly parameter: string;

  constructor(parameter: string) {
    super(
      `Refusing to send "${parameter}": CLM scope is resolved server-side from ` +
        `Entra group claims and is never a client-supplied parameter (NFR-06). ` +
        `If you need to narrow results, use a legitimate filter such as a ` +
        `specific party or contract key — the gateway will still apply the ` +
        `user's scope as a query predicate on top of it.`,
    );
    this.name = 'ClientSuppliedScopeError';
    this.parameter = parameter;
  }
}
